// ============================================
// Predictions — NexDay symbol mapping and sync status
//
// Two tabs (?view=): Mappings (default) and GoPredict S360 (?view=s360&symbol=).
// A mapping has a platform (MT5 | CTRADER); key = platform + symbol.
//
// Standalone page under the Intel menu (path: /predictions).
// Previously lived as a tab inside MT5 Servers → NodeManagement; relocated
// 2026-05 with no content changes. All NexDay logic, helpers, types, and
// the UI body are mirrored exactly from the original tab.
// ============================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { GoPredictS360, type TaigaSymbolRef } from './GoPredictS360';
import { loadPlatformSymbols, type PlatformSymbols } from './predictionsSymbols';

// ============================================================
// ICONS — copied from NodeManagement (the 4 used by PredictionsTab)
// ============================================================
const IcoBook = ({ size = 15 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="m22,0H6C3.794,0,2,1.794,2,4v16c0,2.206,1.794,4,4,4h16c1.103,0,2-.897,2-2V2c0-1.103-.897-2-2-2ZM6,22c-1.103,0-2-.897-2-2s.897-2,2-2h14v4H6Zm16,0h-.675c.114-.313.175-.65.175-1v-1H6c-.71,0-1.37.195-1.938.525C4.021,21.353,4,21.176,4,21V4c0-1.103.897-2,2-2h16v20Z"/>
  </svg>
);

const IcoTrash = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
    <path d="M21,4h-3.1c-.4-2.3-2.4-4-4.9-4h-2c-2.5,0-4.5,1.7-4.9,4H3C2.4,4,2,4.4,2,5s.4,1,1,1h1v14c0,2.2,1.8,4,4,4h8c2.2,0,4-1.8,4-4V6h1c.6,0,1-.4,1-1S21.6,4,21,4Zm-10,16c0,.6-.4,1-1,1s-1-.4-1-1v-7c0-.6.4-1,1-1s1,.4,1,1v7Zm4,0c0,.6-.4,1-1,1s-1-.4-1-1v-7c0-.6.4-1,1-1s1,.4,1,1v7Zm1-14H8.2c.4-1.2,1.5-2,2.8-2h2c1.3,0,2.4.8,2.8,2H16Z"/>
  </svg>
);

const IcoWarning = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
    <path d="m23.119,20.998l-9.49-19.071c-.573-1.151-1.686-1.927-2.629-1.927s-2.056.776-2.629,1.927L-.001,20.998c-.543,1.09-.521,2.327.058,3.399.579,1.072,1.598,1.656,2.571,1.603l18.862-.002c.973.053,1.992-.531,2.571-1.603.579-1.072.601-2.309.058-3.397Zm-11.119.002c-.828,0-1.5-.671-1.5-1.5s.672-1.5,1.5-1.5,1.5.671,1.5,1.5-.672,1.5-1.5,1.5Zm1-5c0,.553-.447,1-1,1s-1-.447-1-1v-8c0-.553.447-1,1-1s1,.447,1,1v8Z"/>
  </svg>
);

const IcoX = ({ size = 13 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="m13.414,12l5.293-5.293c.391-.391.391-1.023,0-1.414s-1.023-.391-1.414,0l-5.293,5.293-5.293-5.293c-.391-.391-1.023-.391-1.414,0s-.391,1.023,0,1.414l5.293,5.293-5.293,5.293c-.391.391-.391,1.023,0,1.414.195.195.451.293.707.293s.512-.098.707-.293l5.293-5.293,5.293,5.293c.195.195.451.293.707.293s.512-.098.707-.293c.391-.391.391-1.023,0-1.414l-5.293-5.293Z"/>
  </svg>
);


// ============================================================
// NEXDAY API — helpers, types
// ============================================================

const NEXDAY_BASE = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

async function ndFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${NEXDAY_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error((err as any).error || `HTTP ${res.status}`);
  }
  return res.json();
}

type MapPlatform = 'MT5' | 'CTRADER';

const platformLabel = (p: MapPlatform | undefined) => (p === 'CTRADER' ? 'cTrader' : 'MT5');

/** 'MT5' | 'mt5' | 'cTrader' | 'CTRADER' -> MapPlatform; anything else null. */
function normalizePlatform(v: unknown): MapPlatform | null {
  const s = String(v ?? '').trim().toUpperCase();
  return s === 'MT5' ? 'MT5' : s === 'CTRADER' ? 'CTRADER' : null;
}

interface NexDayMapping {
  id: number;                // database id (not 1..N)
  platform: MapPlatform;
  symbol: string;
  mt5_symbol: string;        // same value as symbol
  nexday_symbol: string;
  notes?: string | null;
  is_active: boolean;
  nexday_description: string;
  security_type: string;
  current_prediction: string | null;
}

interface NexDayAvailableSymbol {
  nexday_symbol: string;
  description: string;
  security_type: string;
  exchange: string;
  mapped_mt5_count: number;
  mapped_ctrader_count?: number;
  mapped_count?: number;
}

interface UnmappedSymbol {
  platform: MapPlatform;
  symbol: string;
  mt5_symbol: string;
  trader_count: number;
  total_volume: number;
}

interface NexDaySyncStatus {
  endpoint: string;
  status: 'ok' | 'error' | 'stale';
  records_synced: number;
  last_sync_at: string;
  error_message?: string;
}

interface UploadHistoryRow {
  id: number;
  batch_id: string;
  filename: string;
  row_count: number;
  inserted: number;
  updated: number;
  skipped: number;
  errors: number;
  status: 'completed' | 'failed' | 'partial';
  uploaded_at: string;
}

// Rows parsed from Excel but not yet uploaded — shown in preview
interface ParsedRow {
  platform: MapPlatform | null;   // null: the upload's default platform applies
  mt5_symbol: string;
  nexday_symbol: string;
  valid: boolean;
  reason?: string;
}


function ndRelative(iso: string): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000)     return 'just now';
  if (diff < 3_600_000)  return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

async function loadSheetJS(): Promise<any> {
  if ((window as any).XLSX) return (window as any).XLSX;
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
    s.onload  = () => resolve((window as any).XLSX);
    s.onerror = () => reject(new Error('Failed to load SheetJS from CDN'));
    document.head.appendChild(s);
  });
}

// ────────────────────────────────────────────────────────────
// AUTO-MAP HELPER — pure, runs client-side before any API call
// ────────────────────────────────────────────────────────────
type AutoSuggestion = {
  platform:      MapPlatform;
  mt5_symbol:    string;
  nexday_symbol: string;
  exchange:      string;
  confidence:    'exact' | 'prefix';
};

/**
 * Best-effort matching of MT5 symbols against available NexDay symbols.
 *
 * Pass 1 — exact base match:
 *   Strip the exchange suffix from the NexDay symbol (everything after the
 *   first '.'), uppercase both sides, and require equality.
 *   e.g. "EURUSD" matches "EURUSD.FXCM"
 *
 * Pass 2 — prefix match:
 *   Either the NexDay base starts with the MT5 symbol or the MT5 symbol
 *   starts with the NexDay base (handles truncations / contract codes).
 *
 * Symbols with no match in either pass are returned in `unmatched` so the
 * operator knows which ones to map manually.
 */
function computeAutoSuggestions(
  unmapped:  UnmappedSymbol[],
  available: NexDayAvailableSymbol[],
): { matched: AutoSuggestion[]; unmatched: UnmappedSymbol[] } {
  const matched:   AutoSuggestion[] = [];
  const unmatched: UnmappedSymbol[] = [];

  for (const u of unmapped) {
    const sym = u.symbol ?? u.mt5_symbol;
    const platform: MapPlatform = u.platform ?? 'MT5';
    const mt5 = sym.toUpperCase();

    // Pass 1 — exact base
    let hit = available.find(
      a => a.nexday_symbol.split('.')[0].toUpperCase() === mt5,
    );
    if (hit) {
      matched.push({ platform, mt5_symbol: sym, nexday_symbol: hit.nexday_symbol, exchange: hit.exchange, confidence: 'exact' });
      continue;
    }

    // Pass 2 — prefix
    hit = available.find(a => {
      const base = a.nexday_symbol.split('.')[0].toUpperCase();
      return base.startsWith(mt5) || mt5.startsWith(base);
    });
    if (hit) {
      matched.push({ platform, mt5_symbol: sym, nexday_symbol: hit.nexday_symbol, exchange: hit.exchange, confidence: 'prefix' });
      continue;
    }

    unmatched.push(u);
  }

  return { matched, unmatched };
}

// Segmented MT5 / cTrader toggle (Add Mapping, Bulk Upload).
const PlatformToggle = ({ value, onChange, id }: { value: MapPlatform; onChange: (p: MapPlatform) => void; id: string }) => (
  <div id={id} role="radiogroup" aria-label="Platform" className="inline-flex rounded overflow-hidden"
    style={{ border: '1px solid #606060' }}>
    {(['MT5', 'CTRADER'] as MapPlatform[]).map(p => (
      <button key={p} type="button" role="radio" aria-checked={value === p} onClick={() => onChange(p)}
        className="text-xs px-3 py-1 transition-colors"
        style={value === p
          ? { backgroundColor: '#163a3a', color: '#49b3b3' }
          : { backgroundColor: '#232225', color: '#d2d6e2' }}>
        {platformLabel(p)}
      </button>
    ))}
  </div>
);

const PlatformBadge = ({ p }: { p: MapPlatform }) => (
  <span className="px-1.5 py-0.5 rounded font-medium"
    style={{ fontSize: 10, backgroundColor: '#232225', border: `1px solid ${p === 'CTRADER' ? '#c09060' : '#49b3b3'}`,
      color: p === 'CTRADER' ? '#c09060' : '#49b3b3' }}>
    {platformLabel(p)}
  </span>
);

// ============================================================
// PAGE COMPONENT
// ============================================================

export function PredictionsPage() {
  const [mappings,     setMappings]     = useState<NexDayMapping[]>([]);
  const [available,    setAvailable]    = useState<NexDayAvailableSymbol[]>([]);
  const [unmapped,     setUnmapped]     = useState<UnmappedSymbol[]>([]);
  const [syncStatus,   setSyncStatus]   = useState<NexDaySyncStatus[]>([]);
  const [history,      setHistory]      = useState<UploadHistoryRow[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [pToast,       setPToast]       = useState<{ msg: string; type: 'success'|'warn'|'error' } | null>(null);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [deleteId,     setDeleteId]     = useState<number | null>(null);
  const [searchMapped, setSearchMapped] = useState('');
  const [tablePlatform, setTablePlatform] = useState<'ALL' | MapPlatform>('ALL');
  const [platSyms,     setPlatSyms]     = useState<PlatformSymbols | null>(null);
  const [formPlatform, setFormPlatform] = useState<MapPlatform>('MT5');
  const [formMt5,      setFormMt5]      = useState('');
  const [formNexDay,   setFormNexDay]   = useState('');
  const [formNotes,    setFormNotes]    = useState('');
  const [ndSearch,     setNdSearch]     = useState('');
  // Excel upload preview
  const [parsedRows,    setParsedRows]   = useState<ParsedRow[] | null>(null);
  const [bulkPlatform,  setBulkPlatform] = useState<MapPlatform>('MT5');
  const [clearScope,    setClearScope]   = useState<'ALL' | MapPlatform>('ALL');
  const [uploading,     setUploading]    = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Auto-map
  const [autoMatched,   setAutoMatched]   = useState<AutoSuggestion[]>([]);
  const [autoUnmatched, setAutoUnmatched] = useState<UnmappedSymbol[]>([]);
  const [applyingAuto,  setApplyingAuto]  = useState(false);
  const [autoDismissed, setAutoDismissed] = useState(false);
  // Tabs — ?view=s360&platform=MT5&symbol=GOLD (a Taiga symbol); default Mappings.
  const [searchParams, setSearchParams] = useSearchParams();
  const view      = searchParams.get('view') === 's360' ? 's360' : 'mappings';
  const s360Plat  = normalizePlatform(searchParams.get('platform'));
  const s360SymP  = searchParams.get('symbol');
  const s360Sel: TaigaSymbolRef | null = s360Plat && s360SymP ? { platform: s360Plat, symbol: s360SymP } : null;
  const openS360  = (sel: TaigaSymbolRef | null) =>
    setSearchParams(sel ? { view: 's360', platform: sel.platform, symbol: sel.symbol } : { view: 's360' });
  const openMappings = () => setSearchParams({});

  /** Pre-fill the Add Mapping form from an unmapped symbol. */
  const prefill = (u: UnmappedSymbol) => {
    setFormPlatform(u.platform ?? 'MT5');
    setFormMt5(u.symbol ?? u.mt5_symbol);
  };

  const showPToast = (msg: string, type: 'success'|'warn'|'error' = 'success') => {
    setPToast({ msg, type });
    setTimeout(() => setPToast(null), 5000);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, u, st, h] = await Promise.all([
        ndFetch<{ mappings: NexDayMapping[] }>('/api/v1/mappings/nexday'),
        ndFetch<{ unmapped_symbols: UnmappedSymbol[] }>('/api/v1/mappings/nexday/unmapped'),
        ndFetch<{ endpoints: Record<string, NexDaySyncStatus>; health: string }>('/api/v1/predictions/status'),
        ndFetch<{ history: UploadHistoryRow[] }>('/api/v1/mappings/history?limit=10'),
      ]);
      setMappings(m.mappings ?? []);
      setUnmapped(u.unmapped_symbols ?? []);
      setSyncStatus(Object.entries(st.endpoints ?? {}).map(([k, v]) => ({ ...v, endpoint: k })));
      setHistory(h.history ?? []);
    } catch (e: unknown) {
      showPToast((e as Error).message || 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailable = async () => {
    try {
      const r = await ndFetch<{ nexday_symbols: NexDayAvailableSymbol[] }>('/api/v1/mappings/nexday/available');
      setAvailable(r.nexday_symbols ?? []);
    } catch { /* silent — dropdown just stays empty */ }
  };

  useEffect(() => {
    loadAll(); loadAvailable();
    loadPlatformSymbols().then(setPlatSyms).catch(() => { /* picker falls back to typing */ });
  }, []);

  // Recompute auto-suggestions whenever unmapped symbols or available symbols change,
  // but only while no mappings have been configured yet.
  useEffect(() => {
    if (mappings.length > 0 || !available.length || !unmapped.length) {
      setAutoMatched([]);
      setAutoUnmatched([]);
      return;
    }
    const { matched, unmatched } = computeAutoSuggestions(unmapped, available);
    setAutoMatched(matched);
    setAutoUnmatched(unmatched);
  }, [unmapped, available, mappings.length]);

  const handleApplyAutoMap = async () => {
    if (!autoMatched.length) return;
    setApplyingAuto(true);
    try {
      const result = await ndFetch<{
        inserted: number; updated: number; skipped: number;
        errors: { mt5_symbol: string; error: string }[];
      }>('/api/v1/mappings/nexday/bulk', {
        method: 'POST',
        body: JSON.stringify({
          mappings: autoMatched.map(s => ({ platform: s.platform, mt5_symbol: s.mt5_symbol, nexday_symbol: s.nexday_symbol })),
        }),
      });
      const errCount = result.errors?.length ?? 0;
      showPToast(
        `Auto-mapped ${result.inserted} added · ${result.updated} updated${errCount ? ` · ${errCount} errors` : ''}`,
        errCount ? 'warn' : 'success',
      );
      setAutoDismissed(true);
      await loadAll();
    } catch (e: unknown) {
      showPToast((e as Error).message, 'error');
    } finally {
      setApplyingAuto(false);
    }
  };

  const handleAdd = async () => {
    if (!formMt5.trim() || !formNexDay) return;
    // Use the platform's exact symbol name when the typed one matches it ignoring case.
    const typed = formMt5.trim();
    const exact = platSyms?.[formPlatform].find(x => x.symbol.toUpperCase() === typed.toUpperCase())?.symbol ?? typed;
    setSaving(true);
    try {
      await ndFetch('/api/v1/mappings/nexday', {
        method: 'POST',
        body: JSON.stringify({
          platform: formPlatform,
          symbol: exact,
          nexday_symbol: formNexDay,
          ...(formNotes ? { notes: formNotes } : {}),
        }),
      });
      showPToast(`Mapped ${platformLabel(formPlatform)} ${exact} → ${formNexDay}`);
      setFormMt5(''); setFormNexDay(''); setFormNotes(''); setNdSearch('');
      await loadAll();
    } catch (e: unknown) {
      showPToast((e as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await ndFetch(`/api/v1/mappings/nexday/${id}`, { method: 'DELETE' });
      showPToast('Mapping deleted');
      setDeleteId(null);
      await loadAll();
    } catch (e: unknown) {
      showPToast((e as Error).message, 'error');
    }
  };

  const handleClearAll = async () => {
    try {
      const qs = clearScope === 'ALL' ? '' : `&platform=${clearScope}`;
      const r = await ndFetch<{ deleted_count?: number }>(`/api/v1/mappings/nexday?confirm=true${qs}`, { method: 'DELETE' });
      const n = r?.deleted_count;
      const scopeTxt = clearScope === 'ALL' ? '' : ` ${platformLabel(clearScope)}`;
      showPToast(n !== undefined ? `${n}${scopeTxt} mapping${n !== 1 ? 's' : ''} cleared` : `All${scopeTxt} mappings cleared`, 'warn');
      setClearConfirm(false);
      setClearScope('ALL');
      await loadAll();
    } catch (e: unknown) {
      showPToast((e as Error).message, 'error');
    }
  };

  // Step 1: parse the file and show a preview before uploading
  const handleFilePicked = async (file: File) => {
    try {
      const XLSX = await loadSheetJS();
      const data = await file.arrayBuffer();
      const wb   = XLSX.read(data);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      // Use defval: null so empty cells appear as null rather than being omitted
      const rows: Record<string, any>[] = XLSX.utils.sheet_to_json(sheet, { defval: null });

      const parsed: ParsedRow[] = rows.map((row) => {
        // Symbol column: MT5Symbol | Symbol | CTraderSymbol (plus legacy spellings).
        // A CTraderSymbol column with no Platform cell means cTrader.
        const ctCell  = row['CTraderSymbol'] ?? row['ctrader_symbol'] ?? row['cTraderSymbol'];
        const mt5     = (row['MT5Symbol'] ?? row['Symbol'] ?? ctCell ?? row['mt5_symbol'] ?? row['symbol'] ?? row['MT5'] ?? '').toString().trim();
        const nexday  = (row['NexDaySymbol'] ?? row['nexday_symbol'] ?? row['NexDay'] ?? '').toString().trim();
        const platRaw = (row['Platform'] ?? row['platform'] ?? '').toString().trim();
        let platform: MapPlatform | null = null;
        if (platRaw) {
          platform = normalizePlatform(platRaw);
          if (!platform) return { platform: null, mt5_symbol: mt5, nexday_symbol: nexday, valid: false, reason: `Platform "${platRaw}" is not MT5 or cTrader` };
        } else if (ctCell != null && String(ctCell).trim() && row['MT5Symbol'] == null && row['Symbol'] == null) {
          platform = 'CTRADER';
        }
        if (!mt5 && !nexday) return { platform, mt5_symbol: mt5, nexday_symbol: nexday, valid: false, reason: 'Both columns empty' };
        if (!mt5)            return { platform, mt5_symbol: mt5, nexday_symbol: nexday, valid: false, reason: 'Symbol is empty' };
        if (!nexday)         return { platform, mt5_symbol: mt5, nexday_symbol: nexday, valid: false, reason: 'NexDaySymbol is empty' };
        return { platform, mt5_symbol: mt5.toUpperCase(), nexday_symbol: nexday, valid: true };
      });

      setParsedRows(parsed);
    } catch (e: unknown) {
      showPToast((e as Error).message, 'error');
    }
  };

  // Step 2: send valid rows to the API
  const handleConfirmUpload = async () => {
    if (!parsedRows) return;
    const valid = parsedRows.filter(r => r.valid);
    if (!valid.length) { showPToast('No valid rows to upload', 'warn'); return; }

    setUploading(true);
    try {
      const result = await ndFetch<{
        inserted: number; updated: number; skipped: number;
        errors: { mt5_symbol: string; error: string }[];
      }>('/api/v1/mappings/nexday/bulk', {
        method: 'POST',
        body: JSON.stringify({
          platform: bulkPlatform,   // applies to rows without their own Platform
          mappings: valid.map(r => ({
            mt5_symbol: r.mt5_symbol,
            nexday_symbol: r.nexday_symbol,
            ...(r.platform ? { platform: r.platform } : {}),
          })),
        }),
      });
      const errCount = result.errors?.length ?? 0;
      showPToast(
        `${result.inserted} added · ${result.updated} updated · ${result.skipped} skipped${errCount ? ` · ${errCount} errors` : ''}`,
        errCount ? 'warn' : 'success',
      );
      setParsedRows(null);
      await loadAll();
    } catch (e: unknown) {
      showPToast((e as Error).message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const ndOptions = available
    .filter(s => !ndSearch
      || s.nexday_symbol.toLowerCase().includes(ndSearch.toLowerCase())
      || s.description.toLowerCase().includes(ndSearch.toLowerCase()))
    .slice(0, 60);

  const mSym = (m: NexDayMapping) => m.symbol ?? m.mt5_symbol;
  const filteredMappings = mappings.filter(m => {
    if (tablePlatform !== 'ALL' && (m.platform ?? 'MT5') !== tablePlatform) return false;
    if (!searchMapped) return true;
    const q = searchMapped.toLowerCase();
    return mSym(m).toLowerCase().includes(q)
      || m.nexday_symbol.toLowerCase().includes(q)
      || platformLabel(m.platform).toLowerCase().includes(q);
  });
  const ctCount  = mappings.filter(m => m.platform === 'CTRADER').length;
  const mt5Count = mappings.length - ctCount;

  const tabsBar = (
    <div className="flex items-center gap-1 border-b mb-4" style={{ borderColor: '#44454f' }}>
      {([['mappings', 'Mappings'], ['s360', 'GoPredict S360']] as const).map(([k, label]) => (
        <button key={k}
          onClick={() => (k === 'mappings' ? openMappings() : openS360(s360Sel))}
          className="px-4 py-2 text-sm transition-colors"
          style={view === k
            ? { color: '#ffffff', borderBottom: '2px solid #49b3b3', marginBottom: -1 }
            : { color: '#d2d6e2', borderBottom: '2px solid transparent', marginBottom: -1 }}>
          {label}
        </button>
      ))}
    </div>
  );

  const tStyle = (type: string) =>
    type === 'success' ? { backgroundColor: '#162a1c', color: '#66e07a', border: '1px solid #2f6a3d' }
    : type === 'warn'  ? { backgroundColor: '#28220a', color: '#e0d066', border: '1px solid #6a6530' }
    :                    { backgroundColor: '#2c1417', color: '#ff5c5c', border: '1px solid #7a2f36' };

  if (view === 's360') {
    return (
      <div className="h-full p-6 overflow-y-auto">
        {tabsBar}
        <GoPredictS360 selected={s360Sel} onSelect={openS360} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full p-6 overflow-y-auto">
        {tabsBar}
        <div className="flex items-center justify-center py-20">
          <span className="text-sm text-text-muted">Loading NexDay mappings…</span>
        </div>
      </div>
    );
  }

  const validRows   = parsedRows?.filter(r => r.valid)   ?? [];
  const skippedRows = parsedRows?.filter(r => !r.valid)  ?? [];

  return (
    <div className="h-full p-6 overflow-y-auto">
      {tabsBar}
      <div className="space-y-4 relative">
        {/* Toast */}
        {pToast && (
          <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded text-xs font-medium shadow-lg"
            style={tStyle(pToast.type)}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', display: 'inline-block',
              backgroundColor: pToast.type === 'success' ? '#66e07a' : pToast.type === 'warn' ? '#e0d066' : '#ff5c5c' }} />
            {pToast.msg}
          </div>
        )}

        {/* Auto-map suggestions — shown when no mappings exist but matches were found */}
        {!autoDismissed && mappings.length === 0 && autoMatched.length > 0 && (
          <div className="rounded p-4" style={{ backgroundColor: '#0d1f2a', border: '1px solid #1e4a70' }}>
            <div className="flex items-center justify-between mb-3 gap-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text-primary">Auto-Map Suggestions</p>
                <p className="text-xs text-text-muted mt-0.5">
                  {autoMatched.length} symbol{autoMatched.length !== 1 ? 's' : ''} matched automatically by symbol name
                  {autoUnmatched.length > 0
                    ? ` · ${autoUnmatched.length} could not be matched and require manual mapping`
                    : ' · all symbols covered'}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => setAutoDismissed(true)}
                  className="text-xs text-text-muted hover:text-text-primary transition-colors px-2 py-1 rounded"
                  style={{ border: '1px solid #383838' }}>
                  Dismiss
                </button>
                <button onClick={handleApplyAutoMap} disabled={applyingAuto}
                  className="btn btn-primary text-xs px-3 py-1.5"
                  style={applyingAuto ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
                  {applyingAuto ? 'Applying…' : `Apply ${autoMatched.length} mapping${autoMatched.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>

            <div className="rounded overflow-hidden" style={{ border: '1px solid #1e3a5a', backgroundColor: '#0a1520' }}>
              <table className="w-full text-xs">
                <thead style={{ backgroundColor: '#0f2035' }}>
                  <tr>
                    <th className="px-3 py-2 text-left text-text-muted font-medium">Platform</th>
                    <th className="px-3 py-2 text-left text-text-muted font-medium">Symbol</th>
                    <th className="px-3 py-2 text-left text-text-muted font-medium">NexDay Symbol</th>
                    <th className="px-3 py-2 text-left text-text-muted font-medium">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {autoMatched.map(s => (
                    <tr key={`${s.platform}:${s.mt5_symbol}`} className="border-t border-border">
                      <td className="px-3 py-1.5"><PlatformBadge p={s.platform} /></td>
                      <td className="px-3 py-1.5 font-mono font-semibold text-text-primary">{s.mt5_symbol}</td>
                      <td className="px-3 py-1.5 font-mono" style={{ color: '#49b3b3' }}>{s.nexday_symbol}</td>
                      <td className="px-3 py-1.5">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                          style={s.confidence === 'exact'
                            ? { backgroundColor: '#0d1f12', color: '#66e07a', border: '1px solid #1e4d28' }
                            : { backgroundColor: '#1e2a10', color: '#b8d4a5', border: '1px solid #3a5830' }}>
                          {s.confidence === 'exact' ? 'Exact' : 'Prefix'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {autoUnmatched.length > 0 && (
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] text-text-muted flex-shrink-0">No match found for:</span>
                {autoUnmatched.map(u => (
                  <button key={`${u.platform}:${u.symbol ?? u.mt5_symbol}`} onClick={() => prefill(u)}
                    className="font-mono text-[10px] px-1.5 py-0.5 rounded border transition-colors"
                    style={{ backgroundColor: '#1e1a00', border: '1px solid #5a4d00', color: '#c8b040' }}
                    title={`${platformLabel(u.platform)} · click to pre-fill the manual mapping form`}>
                    {u.symbol ?? u.mt5_symbol}{u.platform === 'CTRADER' ? ' (cTrader)' : ''}
                  </button>
                ))}
                <span className="text-[10px] text-text-muted">— use the Add Mapping form.</span>
              </div>
            )}
          </div>
        )}

        {/* Unmapped symbols alert */}
        {unmapped.length > 0 && (
          <div className="rounded p-3 flex items-start gap-3"
            style={{ backgroundColor: '#28220a', border: '1px solid #6a6530' }}>
            <span style={{ color: '#e0d066', flexShrink: 0, marginTop: 1 }}><IcoWarning /></span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold" style={{ color: '#e0d066' }}>
                {unmapped.length} symbol{unmapped.length !== 1 ? 's' : ''} with open positions but no NexDay mapping
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {unmapped.map(u => (
                  <button key={`${u.platform}:${u.symbol ?? u.mt5_symbol}`} onClick={() => prefill(u)}
                    className="flex items-center gap-1.5 rounded px-2 py-1 text-xs transition-colors"
                    style={{ backgroundColor: '#1e1a00', border: '1px solid #5a4d00', color: '#c8b040' }}
                    title={`${platformLabel(u.platform)} · ${u.trader_count} traders · ${u.total_volume.toFixed(1)} lots`}>
                    <span style={{ fontSize: 10, opacity: 0.85 }}>{platformLabel(u.platform)}</span>
                    <span className="font-mono font-semibold">{u.symbol ?? u.mt5_symbol}</span>
                    <span className="opacity-70">{u.total_volume.toFixed(1)}L</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-text-muted mt-1.5">Click a symbol to pre-fill the form below.</p>
            </div>
          </div>
        )}

        {/* Two-column: mappings table + add form */}
        <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 380px' }}>

          {/* LEFT — Active Mappings */}
          <div className="panel flex flex-col overflow-hidden">
            <div className="panel-header">
              <span className="text-sm font-semibold text-text-primary">
                Active Mappings
                <span className="ml-2 text-xs font-normal text-text-muted">
                  {mappings.length} configured{mappings.length > 0 ? ` · MT5 ${mt5Count} · cTrader ${ctCount}` : ''}
                </span>
              </span>
              <div className="flex items-center gap-2">
                <select value={tablePlatform} aria-label="Platform"
                  onChange={e => setTablePlatform(e.target.value as 'ALL' | MapPlatform)}
                  className="bg-[#232225] border border-[#606060] rounded px-2 h-7 text-xs text-white focus:outline-none focus:border-[#49b3b3]">
                  <option value="ALL">All platforms</option>
                  <option value="MT5">MT5</option>
                  <option value="CTRADER">cTrader</option>
                </select>
                <input value={searchMapped} onChange={e => setSearchMapped(e.target.value)}
                  placeholder="Search…" className="input text-xs h-7 w-36" />
                {mappings.length > 0 && (
                  <button onClick={() => setClearConfirm(true)}
                    className="btn text-xs px-2 py-1"
                    style={{ backgroundColor: '#2c1417', color: '#ff5c5c', border: '1px solid #7a2f36' }}>
                    Clear All
                  </button>
                )}
              </div>
            </div>

            {mappings.length === 0 ? (
              <div className="flex-1 flex items-center justify-center py-12">
                <div className="text-center">
                  <p className="text-sm text-text-muted">No mappings configured yet</p>
                  <p className="text-xs text-text-secondary mt-1">Use the form on the right to add your first mapping.</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead style={{ backgroundColor: '#1a1a1e', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr className="text-left">
                      <th className="px-3 py-2 text-text-muted font-medium">Platform</th>
                      <th className="px-3 py-2 text-text-muted font-medium">Symbol</th>
                      <th className="px-3 py-2 text-text-muted font-medium">NexDay Symbol</th>
                      <th className="px-3 py-2 text-text-muted font-medium">Status</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMappings.map(m => (
                      <tr key={m.id} className="border-t border-border hover:bg-surface-hover transition-colors">
                        <td className="px-3 py-2"><PlatformBadge p={m.platform ?? 'MT5'} /></td>
                        <td className="px-3 py-2 font-mono font-semibold text-text-primary" title={m.notes || undefined}>{mSym(m)}</td>
                        <td className="px-3 py-2 font-mono" style={{ color: '#49b3b3' }}>{m.nexday_symbol}</td>
                        <td className="px-3 py-2">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                            style={m.is_active
                              ? { backgroundColor: '#0d1f12', color: '#66e07a', border: '1px solid #1e4d28' }
                              : { backgroundColor: '#2a2a2c', color: '#888', border: '1px solid #444' }}>
                            {m.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => openS360({ platform: m.platform ?? 'MT5', symbol: mSym(m) })}
                            title={`Open ${mSym(m)} in GoPredict S360`}
                            className="text-[10px] px-1.5 py-0.5 rounded transition-colors"
                            style={{ backgroundColor: '#163a3a', color: '#49b3b3', border: '1px solid #2f8f8f' }}>
                            GoPredict
                          </button>
                          {deleteId === m.id ? (
                            <div className="flex items-center gap-1 justify-end">
                              <button onClick={() => handleDelete(m.id)}
                                className="text-[10px] px-1.5 py-0.5 rounded transition-colors"
                                style={{ backgroundColor: '#2c1417', color: '#ff5c5c', border: '1px solid #7a2f36' }}>
                                Confirm
                              </button>
                              <button onClick={() => setDeleteId(null)}
                                className="text-[10px] px-1.5 py-0.5 rounded text-text-muted hover:text-white transition-colors border border-border">
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => setDeleteId(m.id)}
                              className="text-text-muted hover:text-red-400 transition-colors p-1">
                              <IcoTrash />
                            </button>
                          )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredMappings.length === 0 && (searchMapped || tablePlatform !== 'ALL') && (
                      <tr>
                        <td colSpan={5} className="px-3 py-6 text-center text-text-muted">
                          {searchMapped
                            ? `No mappings match "${searchMapped}"`
                            : `No ${platformLabel(tablePlatform as MapPlatform)} symbols are mapped. Add one with the form on the right.`}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* RIGHT — Add form + upload + history */}
          <div className="flex flex-col gap-4">

            {/* Add Mapping */}
            <div className="panel">
              <div className="panel-header">
                <span className="text-sm font-semibold text-text-primary">Add Mapping</span>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label htmlFor="add-platform" className="block text-xs text-text-muted mb-1">Platform</label>
                  <PlatformToggle id="add-platform" value={formPlatform} onChange={setFormPlatform} />
                </div>
                <div>
                  <label className="block text-xs text-text-muted mb-1">
                    {platformLabel(formPlatform)} Symbol
                  </label>
                  <input value={formMt5} onChange={e => setFormMt5(e.target.value)}
                    placeholder={`Search ${platformLabel(formPlatform)} symbols…`} className="input w-full text-sm font-mono mb-1" />
                  {platSyms === null ? (
                    <p className="text-xs text-text-muted mt-1">Loading {platformLabel(formPlatform)} symbols…</p>
                  ) : platSyms[formPlatform].length === 0 ? (
                    <p className="text-xs mt-1" style={{ color: '#c09060' }}>
                      {platformLabel(formPlatform)} symbols unavailable{platSyms.error[formPlatform] ? ` (${platSyms.error[formPlatform]})` : ''}. Type the symbol name.
                    </p>
                  ) : (
                    <div className="rounded border border-border overflow-y-auto" style={{ maxHeight: 160, backgroundColor: '#1a1a1e' }}>
                      {(() => {
                        const q = formMt5.trim().toUpperCase();
                        const mappedTo = new Map(mappings.filter(m => (m.platform ?? 'MT5') === formPlatform).map(m => [mSym(m), m.nexday_symbol]));
                        const opts = platSyms[formPlatform]
                          .filter(x => !q || x.symbol.toUpperCase().includes(q) || x.description.toUpperCase().includes(q))
                          .slice(0, 60);
                        if (!opts.length) return <p className="text-xs text-text-muted p-2 text-center">No matches</p>;
                        return opts.map(x => {
                          const to = mappedTo.get(x.symbol);
                          const sel = formMt5 === x.symbol;
                          return (
                            <button key={x.symbol} type="button" onClick={() => setFormMt5(x.symbol)}
                              className="w-full text-left px-2.5 py-1.5 text-xs flex items-center justify-between gap-2 hover:bg-surface-hover transition-colors"
                              style={sel ? { backgroundColor: '#0d3a3a', color: '#49b3b3' } : {}}>
                              <span className="min-w-0 truncate">
                                <span className="font-mono font-semibold text-text-primary">{x.symbol}</span>
                                {x.description && <span className="ml-2 text-text-muted">{x.description}</span>}
                              </span>
                              {to && (
                                <span className="flex-shrink-0 text-text-muted" style={{ fontSize: 10 }}>
                                  mapped → <span className="font-mono">{to}</span>
                                </span>
                              )}
                            </button>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-text-muted mb-1">
                    NexDay Symbol
                    {formNexDay && <span className="ml-2 font-mono" style={{ color: '#49b3b3' }}>{formNexDay}</span>}
                  </label>
                  <input value={ndSearch}
                    onChange={e => { setNdSearch(e.target.value); setFormNexDay(''); }}
                    placeholder="Search NexDay symbols…" className="input w-full text-xs mb-1" />
                  {available.length > 0 ? (
                    <div className="rounded border border-border overflow-y-auto" style={{ maxHeight: 180, backgroundColor: '#1a1a1e' }}>
                      {ndOptions.length === 0
                        ? <p className="text-xs text-text-muted p-2 text-center">No matches</p>
                        : ndOptions.map(s => (
                          <button key={s.nexday_symbol}
                            onClick={() => { setFormNexDay(s.nexday_symbol); setNdSearch(s.nexday_symbol); }}
                            className="w-full text-left px-2.5 py-1.5 text-xs flex items-center justify-between hover:bg-surface-hover transition-colors"
                            style={formNexDay === s.nexday_symbol ? { backgroundColor: '#0d3a3a', color: '#49b3b3' } : {}}>
                            <span>
                              <span className="font-mono font-semibold text-text-primary">{s.nexday_symbol}</span>
                              {s.security_type && <span className="ml-2 text-text-muted">{s.security_type}</span>}
                            </span>
                          </button>
                        ))
                      }
                      {available.length > 60 && ndOptions.length === 60 && (
                        <p className="text-[10px] text-text-muted text-center py-1.5">Showing top 60 — type to filter</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-text-muted mt-1">Loading available symbols…</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-text-muted mb-1">Notes <span className="font-normal">(optional)</span></label>
                  <input value={formNotes} onChange={e => setFormNotes(e.target.value)}
                    placeholder="e.g. Primary forex pair" className="input w-full text-xs" />
                </div>
                <button onClick={handleAdd} disabled={saving || !formMt5.trim() || !formNexDay}
                  className="w-full btn btn-primary text-sm py-2"
                  style={saving || !formMt5.trim() || !formNexDay ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
                  {saving ? 'Saving…' : 'Add Mapping'}
                </button>
              </div>
            </div>

            {/* Bulk Upload */}
            <div className="panel">
              <div className="panel-header">
                <span className="text-sm font-semibold text-text-primary">Bulk Upload</span>
                <span className="text-xs text-text-muted">.xlsx · .csv</span>
              </div>
              <div className="p-4 space-y-3">
                <div className="rounded p-2.5 text-xs"
                  style={{ backgroundColor: '#1a1a1e', border: '1px solid #2a2a2e' }}>
                  <p className="text-text-muted mb-0.5 font-medium">Expected columns</p>
                  <p className="text-text-primary">MT5Symbol (or Symbol, or CTraderSymbol) · NexDaySymbol · Platform (optional)</p>
                  <p className="text-text-muted mt-0.5">Rows with an empty symbol are skipped — they show in the preview below.</p>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <label htmlFor="bulk-platform" className="text-xs text-text-muted">Platform for rows without one</label>
                  <PlatformToggle id="bulk-platform" value={bulkPlatform} onChange={setBulkPlatform} />
                </div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) { handleFilePicked(f); e.target.value = ''; }}} />
                <button onClick={() => { setParsedRows(null); fileRef.current?.click(); }}
                  className="w-full btn text-sm py-2 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#162a1c', color: '#66e07a', border: '1px solid #2f6a3d' }}>
                  <IcoBook size={14} /> Choose File…
                </button>

                {/* Upload preview */}
                {parsedRows && (
                  <div className="space-y-2">
                    {/* Summary bar */}
                    <div className="flex items-center gap-3 text-xs rounded px-2.5 py-2"
                      style={{ backgroundColor: '#1a1a1e', border: '1px solid #2a2a2e' }}>
                      <span style={{ color: '#66e07a' }}>{validRows.length} will upload</span>
                      <span className="opacity-30">·</span>
                      <span style={{ color: skippedRows.length > 0 ? '#e0d066' : '#666' }}>
                        {skippedRows.length} skipped
                      </span>
                      <button onClick={() => setParsedRows(null)}
                        className="ml-auto text-text-muted hover:text-white transition-colors">
                        <IcoX size={11} />
                      </button>
                    </div>

                    {/* Skipped rows detail */}
                    {skippedRows.length > 0 && (
                      <div className="rounded overflow-hidden"
                        style={{ border: '1px solid #6a6530', backgroundColor: '#1a1600' }}>
                        <div className="px-2.5 py-1.5 text-[10px] font-semibold" style={{ color: '#e0d066', backgroundColor: '#28220a' }}>
                          Skipped rows
                        </div>
                        <div className="overflow-y-auto" style={{ maxHeight: 100 }}>
                          {skippedRows.map((r, i) => (
                            <div key={i} className="flex items-center gap-2 px-2.5 py-1 text-[10px] border-t"
                              style={{ borderColor: '#2a2000' }}>
                              <span className="font-mono text-text-muted w-24 truncate">{r.mt5_symbol || '(empty)'}</span>
                              <span className="opacity-30">→</span>
                              <span className="font-mono" style={{ color: '#c8b040' }}>{r.nexday_symbol || '(empty)'}</span>
                              {r.reason && <span className="ml-auto text-text-muted truncate" title={r.reason}>{r.reason}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Confirm button */}
                    <button onClick={handleConfirmUpload} disabled={uploading || validRows.length === 0}
                      className="w-full btn btn-primary text-sm py-2"
                      style={uploading || validRows.length === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
                      {uploading ? 'Uploading…' : `Upload ${validRows.length} mapping${validRows.length !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Upload History */}
            {history.length > 0 && (
              <div className="panel">
                <div className="panel-header">
                  <span className="text-sm font-semibold text-text-primary">Upload History</span>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: 220 }}>
                  {history.map(h => (
                    <div key={h.id} className="px-3 py-2 border-t border-border first:border-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs font-mono text-text-primary truncate max-w-[160px]" title={h.filename}>
                          {h.filename || h.batch_id}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded"
                          style={h.status === 'completed'
                            ? { backgroundColor: '#0d1f12', color: '#66e07a', border: '1px solid #1e4d28' }
                            : { backgroundColor: '#2c1417', color: '#ff5c5c', border: '1px solid #7a2f36' }}>
                          {h.status}
                        </span>
                      </div>
                      <p className="text-[10px] text-text-muted font-mono">
                        +{h.inserted} / ~{h.updated} / ×{h.skipped} / ⚠{h.errors} · {ndRelative(h.uploaded_at)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Clear All confirm modal */}
        {clearConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,.72)' }}>
            <div className="panel w-full max-w-sm mx-4" style={{ backgroundColor: '#232225' }}>
              <div className="panel-header">
                <span className="text-sm font-semibold" style={{ color: '#ff5c5c' }}>Clear All Mappings</span>
                <button onClick={() => { setClearConfirm(false); setClearScope('ALL'); }} className="btn-icon text-text-muted"><IcoX /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-2 p-3 rounded text-xs"
                  style={{ backgroundColor: '#2a1810', border: '1px solid #7a3f20' }}>
                  <span style={{ color: '#ff5c5c', flexShrink: 0, marginTop: 1 }}><IcoWarning /></span>
                  <span style={{ color: '#e09a55' }}>
                    This will delete <strong>{clearScope === 'ALL' ? mappings.length : clearScope === 'CTRADER' ? ctCount : mt5Count}</strong>
                    {clearScope === 'ALL' ? '' : ` ${platformLabel(clearScope)}`} NexDay mappings.
                    The Signal column will show <strong>—</strong> for these symbols until re-mapped.
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <label htmlFor="clear-scope" className="text-xs text-text-muted">Clear</label>
                  <select id="clear-scope" value={clearScope}
                    onChange={e => setClearScope(e.target.value as 'ALL' | MapPlatform)}
                    className="bg-[#232225] border border-[#606060] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#49b3b3]">
                    <option value="ALL">All platforms ({mappings.length})</option>
                    <option value="MT5" disabled={mt5Count === 0}>MT5 only ({mt5Count})</option>
                    <option value="CTRADER" disabled={ctCount === 0}>cTrader only ({ctCount})</option>
                  </select>
                </div>
                <p className="text-sm text-text-primary">Are you sure you want to clear these mappings?</p>
                <div className="flex items-center justify-end gap-3">
                  <button onClick={() => { setClearConfirm(false); setClearScope('ALL'); }} className="btn btn-ghost text-sm">Cancel</button>
                  <button onClick={handleClearAll}
                    className="btn text-sm"
                    style={{ backgroundColor: '#2c1417', color: '#ff5c5c', border: '1px solid #7a2f36' }}>
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}